import { CLIENT_CATEGORIES, CLIENT_CATEGORY_LABEL, clientCategoryLabel } from "@/lib/cockpit-areas";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ClientBookedServicesSection } from "@/components/admin/ClientBookedServicesSection";
import { lazy, Suspense, useEffect, useMemo, useState, type FormEvent } from "react";

// Die Reiterinhalte kommen erst, wenn der jeweilige Reiter geöffnet wird.
const ClientModulesSection = lazy(async () => ({
  default: (await import("@/components/admin/clients/ClientDetailSections")).ClientModulesSection,
}));
const ClientUsersSection = lazy(async () => ({
  default: (await import("@/components/admin/clients/ClientDetailSections")).ClientUsersSection,
}));
const ClientCoursesSection = lazy(async () => ({
  default: (await import("@/components/admin/clients/ClientDetailSections")).ClientCoursesSection,
}));
const ClientSocialDashboardSection = lazy(async () => ({
  default: (await import("@/components/social/ClientSocialDashboardSection")).ClientSocialDashboardSection,
}));
const ClientQuestionnairesSection = lazy(async () => ({
  default: (await import("@/components/admin/clients/ClientDetailSections"))
    .ClientQuestionnairesSection,
}));

function SectionFallback() {
  return (
    <p className="mt-8 font-body text-sm text-muted-foreground">Bereich wird geladen …</p>
  );
}

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  getClient,
  upsertClient,
  getClientElectionDateSuggestion,
  type ClientCategoryValue,
  setClientStatus,
} from "@/lib/customers.functions";
import {
  MANDATE_TYPES,
  MANDATE_TYPE_LABELS,
  ELECTION_OUTCOMES,
  ELECTION_OUTCOME_LABELS,
  TERM_START_HELP,
  formatDateDe,
  type MandateType,
  type ElectionOutcome,
} from "@/lib/client-mandate";
import type { ClientStatus } from "@/lib/customers.functions";
import {
  inviteCustomerUser,
  listClientUsers,
  setClientUserActive,
  type ClientUserRow,
} from "@/lib/customer-invite.functions";
import { listStaffDirectory } from "@/lib/staff.functions";
import {
  listClientModuleAssignments,
  assignClientModule,
  revokeClientModule,
} from "@/lib/modules.functions";
import {
  CLIENT_MODULE_LABELS,
  MODULE_SOURCE_LABELS,
  activeAssignment,
  lastRevocation,
  formatModuleDate,
} from "@/lib/client-modules";
import { getClientTaskProgress } from "@/lib/module-tasks.functions";
import { progressLabel } from "@/lib/module-tasks";
import { PHASE_TITLES } from "@/lib/hundert-tage";
import { listQuestionnaireOverview } from "@/lib/questionnaire-admin.functions";
import { deriveOnboardingState } from "@/lib/onboarding-status";
import { sendInviteReminder } from "@/lib/invite-reminder.functions";

import {
  getClientCourseCompletions,
  type ClientCourseCompletion,
} from "@/lib/course-completion.functions";

import { useAdminSession } from "@/hooks/use-admin-session";
import { Users, Mail, Ban, RotateCw, CheckCircle2, GraduationCap, Loader2, ClipboardList, CalendarCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ClientInvoicesSection } from "@/components/invoices/ClientInvoicesSection";
import { ClientCredentialHandoversSection } from "@/components/credentials/ClientCredentialHandoversSection";
import { ClientTtpaSection } from "@/components/ttpa/ClientTtpaSection";

import { ClientDangerZone } from "@/components/admin/ClientDangerZone";
import { QuestionnaireNotificationsPanel } from "@/components/admin/QuestionnaireNotificationsPanel";
import { useCourseCoverUrls } from "@/lib/use-course-covers";
import { confirmDialog } from "@/lib/confirm-dialog";



/** Reiter der Kundenakte – der aktive Reiter steht in der Adresse (?tab=…). */
const CLIENT_TABS = [
  { key: "stammdaten", label: "Allgemeines" },
  { key: "mandat", label: "100 Tage Fahrplan" },
  { key: "zugaenge", label: "Zugänge" },
  { key: "passwoerter", label: "Passwörter" },
  { key: "leistungen", label: "Leistungen & Module" },
  { key: "kurse", label: "Kurse" },
  { key: "fragebogen", label: "Fragebögen" },
  { key: "social", label: "Social Media" },
] as const;

type ClientTabKey = (typeof CLIENT_TABS)[number]["key"];

function parseClientTab(value: unknown): ClientTabKey {
  return CLIENT_TABS.some((t) => t.key === value) ? (value as ClientTabKey) : "stammdaten";
}

export const Route = createFileRoute("/_admin/admin/clients/$id")({
  validateSearch: (search: Record<string, unknown>): { tab?: ClientTabKey } =>
    typeof search.tab === "string" ? { tab: parseClientTab(search.tab) } : {},
  component: ClientDetailPage,
});



function ClientDetailPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const isNew = id === "new";
  const session = useAdminSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const requestedTab: ClientTabKey = isNew ? "stammdaten" : parseClientTab(search.tab);
  // Ausgeblendete Reiter fallen auf „Allgemeines“ zurück (Adresszeile inbegriffen).
  const activeTab: ClientTabKey =
    requestedTab === "leistungen" && !session.isAdmin ? "stammdaten" : requestedTab;
  const selectTab = (next: ClientTabKey) => {
    navigate({
      to: "/admin/clients/$id",
      params: { id },
      search: next === "stammdaten" ? {} : { tab: next },
      replace: true,
    });
  };


  const getClientFn = useServerFn(getClient);
  const upsertFn = useServerFn(upsertClient);
  const setStatusFn = useServerFn(setClientStatus);
  const listStaffFn = useServerFn(listStaffDirectory);
  const listUsersFn = useServerFn(listClientUsers);
  const inviteFn = useServerFn(inviteCustomerUser);
  const setActiveFn = useServerFn(setClientUserActive);

  const clientQ = useQuery({
    queryKey: ["admin", "clients", id],
    queryFn: () => getClientFn({ data: { id } }),
    // Ticket AD3: Editorinnen und Editoren dürfen die Kundenakte lesen.
    enabled: !isNew && (session.isAdmin || session.isEditor),
  });
  // Ansprechpartner: Administratoren und Editoren lesen die Namen; ändern darf
  // nur die Administration (siehe `canEditManager`).
  const staffQ = useQuery({
    queryKey: ["admin", "staff-directory"],
    queryFn: () => listStaffFn(),
    enabled: session.isAdmin || session.isEditor,
  });
  const usersQ = useQuery({
    queryKey: ["admin", "client-users", id],
    queryFn: () => listUsersFn({ data: { client_id: id } }),
    enabled: !isNew && (session.isAdmin || session.isEditor),
  });

  const [orgName, setOrgName] = useState("");
  const [status, setStatus] = useState<ClientStatus>("active");
  // Ticket K9: Kategorie steuert die Bereiche im Kundencockpit.
  const [category, setCategory] = useState<ClientCategoryValue>("agenturkunde");
  const [mocoNumber, setMocoNumber] = useState("");
  const [mocoReportUrl, setMocoReportUrl] = useState("");
  const [managerId, setManagerId] = useState<string>("");
  const [initialized, setInitialized] = useState(false);

  // Ticket Z1: Mandatsfelder (ohne Wirkung auf die Cockpit-Sichtbarkeit).
  const [mandateType, setMandateType] = useState<MandateType | "">("");
  const [electionOutcome, setElectionOutcome] = useState<ElectionOutcome>("offen");
  const [electionDay, setElectionDay] = useState("");
  const [termStart, setTermStart] = useState("");

  const suggestionFn = useServerFn(getClientElectionDateSuggestion);
  const suggestionQ = useQuery({
    queryKey: ["admin", "clients", id, "election-date-suggestion"],
    queryFn: () => suggestionFn({ data: { client_id: id } }),
    enabled: !isNew && (session.isAdmin || session.isEditor),
  });
  const electionSuggestion = suggestionQ.data?.election_date ?? null;

  // Hydrate form from loaded client
  if (!isNew && clientQ.data && !initialized) {
    setOrgName(clientQ.data.organization_name);
    setStatus(clientQ.data.status);
    setCategory((clientQ.data.category ?? "") as ClientCategoryValue);
    setMocoNumber(clientQ.data.moco_client_number ?? "");
    setMocoReportUrl((clientQ.data as { moco_report_url?: string | null }).moco_report_url ?? "");
    setManagerId(clientQ.data.account_manager_id ?? "");
    setMandateType(clientQ.data.mandate_type ?? "");
    setElectionOutcome(clientQ.data.election_outcome ?? "offen");
    setElectionDay(clientQ.data.election_day ?? "");
    setTermStart(clientQ.data.term_start ?? "");
    setInitialized(true);
  }

  const saveMutation = useMutation({
    mutationFn: (payload: {
      id?: string;
      organization_name: string;
      status: ClientStatus;
      category: ClientCategoryValue;
      moco_client_number?: string | null;
      moco_report_url: string | null;
      // Nur Administratoren senden den Ansprechpartner mit.
      account_manager_id?: string | null;
      mandate_type: MandateType | null;
      election_outcome: ElectionOutcome;
      election_day: string | null;
      term_start: string | null;
    }) => upsertFn({ data: payload }),
    onSuccess: (row) => {
      toast.success(isNew ? "Kunde angelegt." : "Änderungen gespeichert.");
      queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
      // Das Lösen/Ändern der Moco-Nummer blendet Rechnungen aus – Liste neu laden.
      queryClient.invalidateQueries({ queryKey: ["admin", "client-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["admin-invoices"] });
      if (isNew && row?.id) {
        navigate({ to: "/admin/clients/$id", params: { id: row.id }, replace: true });
      }
    },
    onError: (err: Error) => toast.error(err.message || "Speichern fehlgeschlagen."),
  });




  const statusMutation = useMutation({
    mutationFn: (next: ClientStatus) =>
      setStatusFn({ data: { id, status: next } }),
    onSuccess: (_, next) => {
      setStatus(next);
      toast.success(
        next === "active"
          ? "Kunde aktiviert."
          : next === "archived"
            ? "Kunde archiviert."
            : "Kunde deaktiviert.",
      );
      queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
    },
    onError: (err: Error) => toast.error(err.message || "Statuswechsel fehlgeschlagen."),
  });

  const staff = staffQ.data?.staff ?? [];
  const managerName = useMemo(() => {
    if (!managerId) return null;
    const entry = staff.find((s) => s.id === managerId);
    if (!entry) return null;
    return entry.jobTitle ? `${entry.name} · ${entry.jobTitle}` : entry.name;
  }, [staff, managerId]);
  const clientUsers: ClientUserRow[] = usersQ.data?.users ?? [];
  const activeUserCount = useMemo(() => {
    return clientUsers.filter((u) => u.active && !u.banned).length;
  }, [clientUsers]);

  // Editorinnen und Editoren arbeiten in derselben Ansicht wie Administratoren.
  // Ausnahmen: der Reiter „Leistungen & Module“ und der Gefahrenbereich bleiben
  // Administratoren vorbehalten.
  const isStaffEditor = session.isAdmin || session.isEditor;
  const visibleTabs = useMemo(
    () => CLIENT_TABS.filter((t) => t.key !== "leistungen" || session.isAdmin),
    [session.isAdmin],
  );


  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const org = orgName.trim();
    if (!org) {
      toast.error("Organisationsname ist erforderlich.");
      return;
    }
    saveMutation.mutate({
      id: isNew ? undefined : id,
      organization_name: org,
      status,
      category,
      // Moco-Kundennummer ist nur lesbar; sie wird ausschließlich über den Moco-Abgleich gesetzt.
      moco_report_url: mocoReportUrl.trim() || null,
      // Ansprechpartner ändern ist Administratoren vorbehalten.
      ...(session.isAdmin ? { account_manager_id: managerId || null } : {}),
      // Ticket Z1
      mandate_type: mandateType || null,
      election_outcome: electionOutcome,
      election_day: electionDay || null,
      term_start: termStart || null,
    });
  }

  const c = clientQ.data;
  const isDirty = isNew
    ? orgName.trim().length > 0
    : !!c &&
      (orgName !== c.organization_name ||
        status !== c.status ||
        category !== (c.category ?? "") ||
        mocoReportUrl !== ((c as { moco_report_url?: string | null }).moco_report_url ?? "") ||
        (session.isAdmin && managerId !== (c.account_manager_id ?? "")) ||
        mandateType !== (c.mandate_type ?? "") ||
        electionOutcome !== (c.election_outcome ?? "offen") ||
        electionDay !== (c.election_day ?? "") ||
        termStart !== (c.term_start ?? ""));

  const showForm = activeTab === "stammdaten" || activeTab === "mandat";
  const labelClass = "block text-sm font-medium text-foreground";
  const inputClass =
    "mt-1.5 h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-foreground";

  return (
    <div className="max-w-5xl">
      {/* Kopfbereich: Name, Status und Speichern */}
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-extrabold md:text-4xl">
            {isNew ? "Neuer Kunde" : orgName || "Kunde"}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isNew && clientQ.data && (
            <button
              type="button"
              disabled={statusMutation.isPending}
              onClick={() => statusMutation.mutate(status === "inactive" ? "active" : "inactive")}
              className="inline-flex h-10 items-center rounded-md border border-border px-3 text-sm font-semibold hover:bg-secondary disabled:opacity-60"
            >
              {status === "inactive" ? "Aktivieren" : "Deaktivieren"}
            </button>
          )}
          <button
            type="submit"
            form="client-form"
            disabled={saveMutation.isPending || (!isNew && !isDirty)}
            className="inline-flex h-10 items-center rounded-md bg-brand-button px-5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {saveMutation.isPending
              ? "Speichern…"
              : isNew
                ? "Kunde anlegen"
                : isDirty
                  ? "Änderungen speichern"
                  : "Gespeichert"}
          </button>
        </div>
      </div>

      {/* Reiterleiste */}
      {!isNew && (
        <nav
          aria-label="Bereiche der Kundenakte"
          className="mt-6 -mx-1 flex snap-x gap-1 overflow-x-auto border-b border-border pb-px"
        >
          {visibleTabs.map((tab) => {
            const active = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => selectTab(tab.key)}
                aria-current={active ? "page" : undefined}
                className={
                  "snap-center whitespace-nowrap rounded-t-md px-3 py-2 font-display text-sm font-extrabold transition-colors " +
                  (active
                    ? "border-b-2 border-primary text-foreground"
                    : "border-b-2 border-transparent text-muted-foreground hover:text-foreground")
                }
              >
                {tab.label}
                {tab.key === "zugaenge" && activeUserCount > 0 && (
                  <span className="ml-1.5 text-xs font-semibold text-muted-foreground">
                    {activeUserCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      )}

      {!isNew && clientQ.isLoading ? (
        <div className="mt-8 h-64 animate-pulse rounded-lg border border-border bg-secondary/30" />
      ) : (
        <form
          id="client-form"
          onSubmit={onSubmit}
          hidden={!showForm}
          className="mt-6 rounded-lg border border-border bg-background p-6"
        >
          {/* Stammdaten */}
          <div hidden={activeTab !== "stammdaten"} className="grid gap-5 md:grid-cols-2">
            <div>
              <label className={labelClass}>Organisationsname *</label>
              <input
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                maxLength={200}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ClientStatus)}
                className={inputClass}
              >
                <option value="active">Aktiv</option>
                <option value="archived">Archiviert</option>
                <option value="inactive">Inaktiv</option>
              </select>
              <p className="mt-1.5 text-xs text-muted-foreground">
                „Archiviert“ kommt aus Moco – ohne Einfluss auf den Cockpit-Zugang.
              </p>
            </div>
            <div>
              <label className={labelClass}>Kategorie *</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as ClientCategoryValue)}
                className={inputClass}
              >
                {!clientCategoryLabel(category).ok && (
                  <option value={category}>Unbekannte Kategorie: {category || "leer"}</option>
                )}
                {CLIENT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CLIENT_CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
              {!clientCategoryLabel(category).ok && (
                <p className="mt-1.5 text-xs font-semibold text-destructive">
                  Unbekannte Kategorie: {category || "leer"}. Bitte eine gültige Kategorie wählen.
                </p>
              )}
              {/* Ticket AV6: Der zweite, ausführliche Hinweis am selben Feld ist entfallen. */}
              <p className="mt-1.5 text-xs text-muted-foreground">
                Kurskunden sehen im Cockpit nur Start, Kurse und Profil.
              </p>
            </div>
            <div>
              <label className={labelClass}>Moco-Kundennummer</label>
              <div className="mt-1.5 flex h-10 w-full items-center rounded-md border border-border bg-secondary/40 px-3 font-mono text-sm text-muted-foreground">
                {mocoNumber || "keine Verknüpfung"}
              </div>
              {/*
                Ticket AV6: Langfassung entfernt. Fachlich: Verknüpfen und Lösen erfolgt
                im Moco-Abgleich; beim Lösen werden dort importierte Rechnungen im
                Cockpit ausgeblendet, manuell hochgeladene bleiben unberührt.
              */}
              <p className="mt-1.5 text-xs text-muted-foreground">
                Wird nur über den Moco-Abgleich gesetzt.
              </p>
            </div>
            <div>
              <label className={labelClass}>Moco-Berichtslink</label>
              <input
                value={mocoReportUrl}
                onChange={(e) => setMocoReportUrl(e.target.value)}
                type="url"
                inputMode="url"
                maxLength={2000}
                placeholder="https://…"
                className={inputClass}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                Optional. Erscheint im Cockpit als Karte „Projektbericht“.
              </p>
            </div>
            <div>
              <label className={labelClass}>Ansprechpartner (creategy)</label>
              {session.isAdmin ? (
                <>
                  <select
                    value={managerId}
                    onChange={(e) => setManagerId(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">— Kein Ansprechpartner —</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                        {s.jobTitle ? ` · ${s.jobTitle}` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Auswahl aus internen Mitarbeitenden.
                  </p>
                </>
              ) : (
                <>
                  <div className="mt-1.5 flex h-10 w-full items-center rounded-md border border-border bg-secondary/40 px-3 text-sm text-muted-foreground">
                    {managerName ?? "— Kein Ansprechpartner —"}
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Änderung nur durch Administratoren.
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Mandat & 100 Tage – rein informativ, keine Wirkung auf Cockpit-Bereiche. */}
          <div hidden={activeTab !== "mandat"}>
            <p className="text-xs text-muted-foreground">
              Diese Angaben dienen der Amtszeit-Planung. Sie haben keinen Einfluss auf die
              Kategorie oder die freigegebenen Cockpit-Bereiche.
            </p>
            <div className="mt-4 grid gap-5 md:grid-cols-2">
              <div>
                <label className={labelClass}>Mandatstyp</label>
                <select
                  value={mandateType}
                  onChange={(e) => setMandateType(e.target.value as MandateType | "")}
                  className={inputClass}
                >
                  <option value="">— nicht gesetzt —</option>
                  {MANDATE_TYPES.map((m) => (
                    <option key={m} value={m}>
                      {MANDATE_TYPE_LABELS[m]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Wahlausgang</label>
                <select
                  value={electionOutcome}
                  onChange={(e) => setElectionOutcome(e.target.value as ElectionOutcome)}
                  className={inputClass}
                >
                  {ELECTION_OUTCOMES.map((o) => (
                    <option key={o} value={o}>
                      {ELECTION_OUTCOME_LABELS[o]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Wahltag</label>
                <input
                  type="date"
                  value={electionDay}
                  onChange={(e) => setElectionDay(e.target.value)}
                  className={inputClass}
                />
                {electionSuggestion && electionSuggestion !== electionDay && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>Vorschlag: {formatDateDe(electionSuggestion)} (Fragebogen)</span>
                    <button
                      type="button"
                      onClick={() => setElectionDay(electionSuggestion)}
                      className="inline-flex h-7 items-center rounded-md border border-border px-2 font-semibold hover:bg-secondary"
                    >
                      Übernehmen
                    </button>
                  </div>
                )}
                <p
                  className="mt-1.5 text-xs text-muted-foreground"
                  title="Führend ist immer dieses Feld am Kunden; Fragebogen-Werte werden nie automatisch übernommen."
                >
                  Führend ist immer dieses Feld.
                </p>
              </div>
              <div>
                <label className={labelClass}>Amtsantritt</label>
                <input
                  type="date"
                  value={termStart}
                  onChange={(e) => setTermStart(e.target.value)}
                  className={inputClass}
                />
                <p className="mt-1.5 text-xs text-muted-foreground" title={TERM_START_HELP}>
                  {TERM_START_HELP}
                </p>
              </div>
            </div>
          </div>

          {isNew && (
            <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-border pt-5">
              <Link
                to="/admin/clients"
                className="inline-flex h-10 items-center rounded-md border border-border px-4 text-sm font-semibold hover:bg-secondary"
              >
                Abbrechen
              </Link>
              <button
                type="submit"
                disabled={saveMutation.isPending}
                className="inline-flex h-10 items-center rounded-md bg-brand-button px-5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {saveMutation.isPending ? "Speichern…" : "Kunde anlegen"}
              </button>
            </div>
          )}
        </form>
      )}

      {!isNew && !clientQ.isLoading && (
        <Suspense fallback={<SectionFallback />}>
          {activeTab === "mandat" && <ClientModulesSection clientId={id} />}

          {activeTab === "zugaenge" && (
            <ClientUsersSection
              clientId={id}
              isAdmin={isStaffEditor}
              users={clientUsers}
              isLoading={usersQ.isLoading}
              activeCount={activeUserCount}
              {...(isStaffEditor ? { inviteFn, setActiveFn } : {})}
            />
          )}


          {activeTab === "passwoerter" && <ClientCredentialHandoversSection clientId={id} />}

          {activeTab === "leistungen" && session.isAdmin && (
            <>
              <ClientBookedServicesSection clientId={id} />
              <ClientInvoicesSection clientId={id} />
            </>
          )}

          {activeTab === "kurse" && <ClientCoursesSection clientId={id} clientStatus={status} />}

          {activeTab === "fragebogen" && (
            <>
              <ClientQuestionnairesSection clientId={id} clientStatus={status} />
              <ClientTtpaSection clientId={id} />
            </>
          )}

          {activeTab === "social" && (
            <ClientSocialDashboardSection clientId={id} clientName={orgName} />
          )}

          {activeTab === "stammdaten" && session.isAdmin && (
            <ClientDangerZone clientId={id} />
          )}
        </Suspense>
      )}

    </div>
  );
}



